import React, { createContext, useContext, useMemo, useState } from "react";
import { useSubscription } from "@apollo/client";
import { NOTIFICATION_ADDED_SUBSCRIPTION } from "../components/event/messenger/graphQl/MessengerAPI";
import { useZorni } from "./useZorni";

// 🧩 Création du contexte global
const NotificationContext = createContext();

// La subscription `messageReceived` (Message) a été supprimée côté backend.
// `notificationAdded` (MESSAGE | TASK | RESERVATION) est l'unique canal temps réel désormais.
// Miroir de NotificationTypeEnum (zorni-controller: src/common/model/enum/notificationTypeEnum.ts)
const NOTIFICATION_TYPE = {
  MESSAGE: "MESSAGE",
  TASK: "TASK",
  RESERVATION: "RESERVATION",
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, userId } = useZorni();
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [hasNewReservations, setHasNewReservations] = useState(false);
  // TASK (ou tout futur type sans indicateur dédié) : pas encore de page pour les consommer/lire ;
  // compté ici uniquement pour le point rouge global du header ("quel que soit son type").
  const [hasNewOtherNotifications, setHasNewOtherNotifications] = useState(false);

  // 🔄 Abonnement temps réel via WebSocket
  useSubscription(NOTIFICATION_ADDED_SUBSCRIPTION, {
    skip: !isAuthenticated || !userId,
    onData: ({ data }) => {
      const notification = data?.data?.notificationAdded;
      if (!notification) return;

      switch (notification.notificationType) {
        case NOTIFICATION_TYPE.MESSAGE:
          setHasNewMessages(true); // ✅ Indique qu’il y a de nouveaux messages
          break;
        case NOTIFICATION_TYPE.RESERVATION:
          setHasNewReservations(true);
          break;
        default:
          setHasNewOtherNotifications(true);
          break;
      }
    },
  });

  // 🧹 Notifications de type message traitées (appelé depuis Messenger à la consultation)
  const clearMessages = () => {
    setHasNewMessages(false);
  };

  // 🧹 Notifications de type réservation traitées (appelé depuis MyTrips à la consultation)
  const clearReservations = () => {
    setHasNewReservations(false);
  };

  // 🔴 Point rouge global du header : au moins une notification non lue, tout type confondu.
  const hasUnreadNotifications = useMemo(
    () => hasNewMessages || hasNewReservations || hasNewOtherNotifications,
    [hasNewMessages, hasNewReservations, hasNewOtherNotifications]
  );

  // ✅ Valeurs exposées au contexte
  const value = {
    hasNewMessages,
    clearMessages,

    hasNewReservations,
    clearReservations,

    hasUnreadNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// 🪝 Hook d’accès au contexte
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
